import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import LoadingState from '../common/LoadingState';
import { AlertTriangle, BarChart3, ClipboardList, Timer } from 'lucide-react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ClockIcon,
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  PresentationChartBarIcon,
  DocumentChartBarIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline';

interface AgendaManagerProps {
  assemblyId: string;
  userRole: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
  assemblyStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
}

interface AgendaItem {
  id: string;
  assemblyId: string;
  order: number;
  title: string;
  description?: string;
  type: 'presentation' | 'discussion' | 'voting' | 'report' | 'announcement';
  estimatedDuration: number;
  votingType?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  attachments: string[];
  notes?: string;
  presenterName?: string;
  votingSessionsCount?: number;
}

interface NewAgendaItem {
  title: string;
  description: string;
  type: 'presentation' | 'discussion' | 'voting' | 'report' | 'announcement';
  estimatedDuration: number;
  votingType?: string;
  presenterName?: string;
  notes?: string;
}

const MIN_LOADING_MS = 250;

const ensureMinimumLoadingTime = async (startTime: number) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
  }
};

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';

const AgendaManager: React.FC<AgendaManagerProps> = ({ assemblyId, userRole, assemblyStatus }) => {
  const { confirm } = useConfirm();
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUrgentWarning, setShowUrgentWarning] = useState(false);
  const [editingItem, setEditingItem] = useState<AgendaItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);
  const [newItem, setNewItem] = useState<NewAgendaItem>({
    title: '',
    description: '',
    type: 'discussion',
    estimatedDuration: 15,
    presenterName: '',
    notes: ''
  });

  const typeIcons = {
    presentation: PresentationChartBarIcon,
    discussion: ChatBubbleBottomCenterTextIcon,
    voting: DocumentChartBarIcon,
    report: DocumentTextIcon,
    announcement: SpeakerWaveIcon
  };

  const typeLabels = {
    presentation: 'Presentación',
    discussion: 'Discusión',
    voting: 'Votación',
    report: 'Informe',
    announcement: 'Anuncio'
  };

  const typeToneClasses = {
    presentation: 'border-blue-200 bg-blue-50 text-blue-700',
    discussion: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    voting: 'border-violet-200 bg-violet-50 text-violet-700',
    report: 'border-amber-200 bg-amber-50 text-amber-700',
    announcement: 'border-rose-200 bg-rose-50 text-rose-700'
  };

  const typeAccentClasses = {
    presentation: 'border-l-blue-500',
    discussion: 'border-l-emerald-500',
    voting: 'border-l-violet-500',
    report: 'border-l-amber-500',
    announcement: 'border-l-rose-500'
  };

  const statusLabels = {
    pending: 'Pendiente',
    in_progress: 'En Progreso',
    completed: 'Completado',
    skipped: 'Omitido',
  };

  // Cargar agenda items al montar el componente
  useEffect(() => {
    loadAgendaItems();
  }, [assemblyId]);

  const loadAgendaItems = async () => {
    const startTime = Date.now();
    setIsLoadingAgenda(true);
    try {
      console.log('🔍 Cargando agenda items para assemblyId:', assemblyId);
      const response = await apiService.getAgendaItemsManagerView(assemblyId);
      console.log('📡 Respuesta del API:', response.data);
      
      const itemsData = response.data.data || response.data;
      console.log('📋 Items procesados:', itemsData);
      
      setAgendaItems(Array.isArray(itemsData) ? itemsData : []);
      console.log('✅ Items establecidos en el estado');
    } catch (error) {
      console.error('❌ Error al cargar agenda items:', error);
    } finally {
      await ensureMinimumLoadingTime(startTime);
      setIsLoadingAgenda(false);
    }
  };

  const handleCreateItem = async () => {
    if (!newItem.title.trim()) {
      return;
    }

    // ⚠️ Si la asamblea está activa, mostrar advertencia especial
    if (assemblyStatus === 'in_progress' && userRole === 'admin' && !showUrgentWarning) {
      setShowUrgentWarning(true);
      return;
    }

    setIsLoading(true);
    try {
      const nextOrder = agendaItems.length > 0 ? Math.max(...agendaItems.map(item => item.order)) + 1 : 1;
      
      const itemData = {
        assemblyId,
        order: nextOrder,
        ...newItem,
        attachments: [],
        // Marcar como urgente si se agrega durante asamblea activa
        notes: assemblyStatus === 'in_progress' 
          ? `[URGENTE - Agregado durante asamblea] ${newItem.notes || ''}`.trim()
          : newItem.notes
      };

      const response = await apiService.createAgendaItem(itemData);
      console.log('Agenda item creado:', response.data);
      
      // Recargar items
      await loadAgendaItems();
      
      // Resetear formulario
      setNewItem({
        title: '',
        description: '',
        type: 'discussion',
        estimatedDuration: 15,
        presenterName: '',
        notes: ''
      });
      
      setShowCreateForm(false);
      setShowUrgentWarning(false); // Reset warning flag
    } catch (error) {
      console.error('Error al crear agenda item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditItem = (item: AgendaItem) => {
    setEditingItem(item);
    setNewItem({
      title: item.title,
      description: item.description || '',
      type: item.type,
      estimatedDuration: item.estimatedDuration,
      votingType: item.votingType,
      presenterName: item.presenterName || '',
      notes: item.notes || ''
    });
    setShowCreateForm(true);
  };

  const handleUpdateItem = async () => {
    if (!editingItem || !newItem.title.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.updateAgendaItem(editingItem.id, newItem);
      console.log('Agenda item actualizado:', response.data);
      
      // Recargar items
      await loadAgendaItems();
      
      // Resetear formulario
      setEditingItem(null);
      setNewItem({
        title: '',
        description: '',
        type: 'discussion',
        estimatedDuration: 15,
        presenterName: '',
        notes: ''
      });
      
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error al actualizar agenda item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    const shouldDelete = await confirm({
      title: 'Eliminar punto de agenda',
      message: '¿Estás seguro de que quieres eliminar este punto de agenda?',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.deleteAgendaItem(itemId);
      console.log('Agenda item eliminado:', itemId);
      
      // Recargar items
      await loadAgendaItems();
    } catch (error) {
      console.error('Error al eliminar agenda item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setShowCreateForm(false);
    setNewItem({
      title: '',
      description: '',
      type: 'discussion',
      estimatedDuration: 15,
      presenterName: '',
      notes: ''
    });
  };

  // Los propietarios pueden VER la agenda pero no GESTIONARLA
  // Solo bloqueamos acceso total a invitados/inquilinos
  if (userRole === 'tenant' || userRole === 'guest') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <div className="text-center">
          <DocumentTextIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
          <h3 className="mb-2 text-lg font-medium text-slate-900">
            Acceso Restringido
          </h3>
          <p className="text-slate-600">
            No tienes permisos para ver la agenda de esta asamblea.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mensaje informativo para propietarios (solo lectura) */}
      {userRole === 'owner' && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="mr-2 h-5 w-5 text-blue-600" />
            <p className="text-sm text-blue-800">
              <strong>Vista de solo lectura:</strong> Puedes consultar la agenda de la asamblea, pero solo los administradores pueden crear o modificar puntos.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`${shellCardClassName} p-5 md:p-6`}>
        <div className="mb-4 flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight text-slate-900">
              {editingItem ? 'Editar Punto de Agenda' : userRole === 'admin' ? 'Gestión de Agenda' : 'Agenda de la Asamblea'}
            </h3>
          </div>
          {!showCreateForm && userRole === 'admin' && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 hover:shadow-md"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Nuevo Punto
            </button>
          )}
        </div>

        {showCreateForm && userRole === 'admin' ? (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 md:p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Título *
                </label>
                <input
                  type="text"
                  value={newItem.title}
                  onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  placeholder="Título del punto de agenda"
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Tipo
                </label>
                <select
                  value={newItem.type}
                  onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="presentation">Presentación</option>
                  <option value="discussion">Discusión</option>
                  <option value="voting">Votación</option>
                  <option value="report">Informe</option>
                  <option value="announcement">Anuncio</option>
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Descripción
              </label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Descripción del punto de agenda..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Duración (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={newItem.estimatedDuration}
                  onChange={(e) => setNewItem(prev => ({ ...prev, estimatedDuration: parseInt(e.target.value) }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Presentador
                </label>
                <input
                  type="text"
                  value={newItem.presenterName}
                  onChange={(e) => setNewItem(prev => ({ ...prev, presenterName: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  placeholder="Nombre del presentador"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Notas
              </label>
              <textarea
                value={newItem.notes}
                onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Notas adicionales..."
              />
            </div>

            {/* ⚠️ Advertencia de punto urgente durante asamblea activa */}
            {assemblyStatus === 'in_progress' && (
              <div className="rounded-xl border-2 border-orange-300 bg-orange-50 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-semibold text-orange-800">
                      <span className="inline-flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-700" aria-hidden="true" />
                        <span>La asamblea está en curso</span>
                      </span>
                    </h3>
                    <div className="mt-2 text-sm text-orange-700">
                      <p className="mb-2">
                        Estás agregando un punto <strong>fuera de la agenda original</strong>. Este punto será marcado como <strong>"URGENTE"</strong> y visible para todos los participantes.
                      </p>
                      <p className="text-xs text-orange-600">
                        Los participantes recibirán una notificación de que se agregó un punto urgente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={handleCancelEdit}
                className="rounded-xl border border-slate-300 px-4 py-2 text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={editingItem ? handleUpdateItem : handleCreateItem}
                disabled={isLoading || !newItem.title.trim()}
                className="rounded-xl bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingItem ? 'Actualizando...' : 'Creando...'}
                  </div>
                ) : (
                  editingItem ? 'Actualizar Punto' : 'Crear Punto'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            {isLoadingAgenda ? (
              <LoadingState />
            ) : agendaItems.length > 0 ? (
              <div>
                <h4 className="mb-4 text-lg font-medium text-slate-900">
                  Puntos de Agenda ({agendaItems.length})
                </h4>
                <div className="space-y-3">
                  {agendaItems.map((item, index) => {
                    const IconComponent = typeIcons[item.type];
                    return (
                        <div
                          key={item.id}
                          className={`rounded-2xl border border-slate-200 border-l-4 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md md:p-5 ${typeAccentClasses[item.type]}`}
                        >
                          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div className="flex min-w-0 flex-1 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
                                <span className="text-xs font-semibold">#{item.order}</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <IconComponent className="h-5 w-5 text-slate-400" />
                                  <h5 className="break-words font-medium text-slate-900">{item.title}</h5>
                                  {item.notes?.includes('[URGENTE') && (
                                    <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
                                      <AlertTriangle className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                      <span>URGENTE</span>
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="mt-1 break-words text-sm text-slate-600">{item.description}</p>
                                )}
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-500 md:gap-3">
                                  {item.status && (
                                    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                                      item.status === 'pending' 
                                        ? 'border-amber-200 bg-amber-50 text-amber-800' 
                                        : item.status === 'in_progress'
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                        : item.status === 'completed'
                                        ? 'border-blue-200 bg-blue-50 text-blue-800'
                                        : 'border-slate-200 bg-slate-50 text-slate-800'
                                    }`}>
                                      {statusLabels[item.status]}
                                    </span>
                                  )}
                                  <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${typeToneClasses[item.type]}`}>
                                    <span className="inline-flex items-center gap-1.5">
                                      <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{typeLabels[item.type]}</span>
                                    </span>
                                  </span>
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                                    <span className="inline-flex items-center gap-1.5">
                                      <Timer className="h-3.5 w-3.5" aria-hidden="true" />
                                      <span>{item.estimatedDuration} min</span>
                                    </span>
                                  </span>
                                  {(item.votingSessionsCount ?? 0) > 0 && (
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                                      <span className="inline-flex items-center gap-1.5">
                                        <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
                                        <span>{item.votingSessionsCount} sesiones</span>
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {userRole === 'admin' && (
                              <div className="flex items-center gap-2 md:ml-4 md:justify-end">
                                <button
                                  onClick={() => handleEditItem(item)}
                                  disabled={assemblyStatus === 'in_progress'}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  title={assemblyStatus === 'in_progress' ? 'No se puede editar durante asamblea activa' : 'Editar'}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  disabled={assemblyStatus === 'in_progress'}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  title={assemblyStatus === 'in_progress' ? 'No se puede eliminar durante asamblea activa' : 'Eliminar'}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                <DocumentTextIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <h4 className="mb-2 text-lg font-medium text-slate-900">
                  Sin Puntos de Agenda
                </h4>
                <p className="text-slate-600">
                  No hay puntos de agenda creados. Crea el primer punto para comenzar.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmación para punto urgente */}
      {showUrgentWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" aria-hidden="true" />
                    <span>Agregar Punto Urgente</span>
                  </span>
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  La asamblea está <strong>activa</strong>. Este punto será agregado como <strong>"URGENTE"</strong> y notificado a todos los participantes conectados.
                </p>
                <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <p className="text-xs text-orange-800">
                    <strong>Punto a agregar:</strong> {newItem.title}
                  </p>
                </div>
                <p className="text-xs text-gray-600">
                  ¿Estás seguro de continuar?
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowUrgentWarning(false);
                }}
                className="rounded-xl border border-slate-300 px-4 py-2 text-slate-700 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  // Confirmar y continuar con la creación
                  handleCreateItem();
                }}
                className="rounded-xl bg-orange-600 px-4 py-2 font-semibold text-white transition hover:bg-orange-700"
              >
                Sí, Agregar Punto Urgente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaManager;